import { Input, ScrollArea, Stack, Text } from "@mantine/core";
import { type JSX, useEffect, useMemo, useRef, useState } from "react";
import { RadioCard } from "@/common-ui";
import type { CustomerDetailsPayload } from "@/customer-info";
import classes from "./CustomerVerificationUpdated.module.css";

const CustomerType = Object.freeze({
    Primary: "Primary",
    Authorized: "Authorized",
    Custom: "Someone else",
});

type CustomerType = (typeof CustomerType)[keyof typeof CustomerType];

interface CustomerProps {
    name: string;
    type: CustomerType;
}

interface CustomerNamesSectionProps {
    customer?: CustomerDetailsPayload;
    onOptionSelect: (input?: string) => void;
    isDisabled: boolean;
    /**
     * Required key prop to reset component state when needed.
     * Pass a unique value (like session ID) to reset selected state.
     */
    key: string | number;
}

const SOMEONE_ELSE = "Someone else";

export function CustomerNamesSection({ customer, onOptionSelect, isDisabled }: CustomerNamesSectionProps): JSX.Element {
    const [selected, setSelected] = useState("");
    const focusRef = useRef<HTMLInputElement>(null);

    const onRadioButtonSelected = (name: string) => {
        onOptionSelect(undefined);
        setSelected(name);

        if (name === SOMEONE_ELSE) {
            // This puts the focus operation after the render cycle ensuring the input is available in the DOM
            setTimeout(() => focusRef.current?.focus(), 0);
        } else {
            // For any other option, just pass the selected name to the parent
            onOptionSelect(name);
        }
    };

    // -- This effect is intended to run cleanup on unmount only
    // ! handles edge case allowing the user to select a name and then change the customer mdn without having to reselect a name.
    // biome-ignore lint/correctness/useExhaustiveDependencies: onOptionSelect changes often and causes unintended behavior if included in the dependency array
    useEffect(() => {
        return () => onOptionSelect(undefined);
    }, []);

    const onInputChange = ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        onOptionSelect(value);
    };

    const customerProps = useMemo<CustomerProps[]>(() => {
        if (!customer?.primaryAccountHolder?.fullName) {
            return [];
        }

        const primaryAccountHolder = {
            name: customer.primaryAccountHolder.fullName,
            type: CustomerType.Primary,
        };

        const authorizedUsers = customer.authorizedUsers
            ? customer.authorizedUsers
                  .filter((user) => !!user.fullName)
                  .map((user) => ({
                      name: user.fullName!,
                      type: CustomerType.Authorized,
                  }))
            : [];

        return [primaryAccountHolder, ...authorizedUsers, { name: SOMEONE_ELSE, type: CustomerType.Custom }];
    }, [customer]);

    return (
        <>
            <Text c="dark.1" size="sm" mb="md" mt="xxl" id="customer-name-question">
                What’s their name?
            </Text>
            {customerProps.length > 0 && (
                <ScrollArea type="auto" h={customerProps.length > 2 ? 160 : 120} scrollbarSize={5}>
                    {/* TODO: add responsive design to this section so that it can handle more than 3 names if we have a ton of free space */}
                    <Stack gap={0} mb="md" role="radiogroup" aria-labelledby="customer-name-question">
                        {customerProps.map((item) => {
                            return (
                                <RadioCard
                                    disabled={isDisabled}
                                    className={`${selected === item.name ? classes.radioSelected : classes.radio} fs-mask`} //TODO: selected can be managed via CSS modules and data-selected rules
                                    color="var(--mantine-color-dark-5)"
                                    onClick={() => {
                                        onRadioButtonSelected(item.name);
                                    }}
                                    selected={selected === item.name}
                                    key={item.name}
                                    aria-label={item.name}
                                    id={`customer-name-${item.name}`}
                                    label={item.type === CustomerType.Custom ? item.name : item.name.toLowerCase()}
                                    value={item.name}
                                    analytics={{
                                        actionId: "CustomerNameSelected",
                                    }}
                                    radioProps={{ tt: item.type === CustomerType.Custom ? "none" : "capitalize" }}
                                />
                            );
                        })}
                    </Stack>
                </ScrollArea>
            )}
            {(selected as CustomerType) === CustomerType.Custom || customerProps.length === 0 ? (
                <Input
                    aria-label="Enter customer name"
                    aria-describedby="customer-name-question"
                    ref={focusRef}
                    id="customer-name-input"
                    disabled={isDisabled}
                    mt="0px"
                    mb="0px"
                    size="md"
                    onChange={onInputChange}
                    className="fs-mask"
                    classNames={{ wrapper: classes.styledInputWrapper, input: classes.styledInput }}
                    placeholder="Enter name"
                />
            ) : null}
        </>
    );
}
